/**
 * شاشة «تحديد كلمة السر» — الصفحة اللي رابط الدعوة و«نسيت كلمة السر» بيوديها.
 *
 * 🔴 **كانت ناقصة تمامًا زي شاشة تأكيد البريد (اتكشفت ٢٠٢٦-٠٩-١٩).**
 *    الباك إند بيبعت `/set-password?token=…`، والواجهة مكانش فيها المسار —
 *    فرابط الدعوة ورابط استعادة كلمة السر الاتنين كانوا بيوديوا لصفحة
 *    الدخول. يعني **مفيش أي طريق لاستعادة كلمة سر في السيستم كله**، وكل
 *    نسيان كان لازم يتحل بكلمة سر مؤقتة من المالك بالإيد.
 *
 * 🔑 التوكن **استخدام واحد**، والنجاح بيلغي كل الجلسات — أي جهاز داخل
 *    بالحساب ده بيتقفل. وده مقصود: اللي بيغيّر كلمة سره غالبًا بيغيّرها
 *    لأن فيه حاجة مقلقاه.
 */
import { useState } from "react";
import { Icon } from "@/components/Icon";
import { PasswordReveal } from "@/components/PasswordReveal";
import { Link, LOGIN_PATH, useRouter } from "@/lib/router";
import { errorMessageAr, OfflineError } from "@/api/client";
import { setPasswordWithToken } from "@/api/auth";
import "@/styles/auth.css";

/** نفس الحد الأدنى بتاع السيرفر — الرفض هنا أسرع وأوضح من رد خطأ. */
const MIN_LENGTH = 8;

export function SetPassword() {
  const { navigate } = useRouter();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (pw.length < MIN_LENGTH) {
      setError(`كلمة السر لازم تكون ${MIN_LENGTH} حروف على الأقل`);
      return;
    }
    if (pw !== pw2) {
      setError("الكلمتين مش زي بعض");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await setPasswordWithToken(token, pw);
      setDone(true);
    } catch (err) {
      if (!(err instanceof OfflineError)) {
        console.error("[set-password] تحديد كلمة السر فشل", err);
      }
      setError(errorMessageAr(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth" dir="rtl">
      <section className="auth-pane form">
        <div className="auth-box">
          <div className="auth-brand">
            <div className="brand-mark">
              <Icon name="book" size={20} stroke="#0F1D33" />
            </div>
            <div>
              <div className="brand-name">رتّل</div>
              <div className="brand-sub">السيستم الإداري</div>
            </div>
          </div>

          {!token ? (
            <div style={{ marginTop: 40 }}>
              <div className="auth-title">الرابط ناقص</div>
              <div className="t-desc" style={{ marginTop: 10 }}>
                افتح الرابط من الرسالة اللي وصلتك كامل — ولو نسخته بإيدك يمكن
                يكون اتقص.
              </div>
              <div style={{ marginTop: 28 }}>
                <Link to={LOGIN_PATH} className="link">
                  الرجوع لصفحة الدخول
                </Link>
              </div>
            </div>
          ) : done ? (
            <div style={{ marginTop: 40 }}>
              <div className="auth-title">تمام — كلمة السر اتحددت</div>
              <div className="t-desc" style={{ marginTop: 10 }}>
                تقدر تسجّل دخول بيها دلوقتي.
              </div>
              {/* ⚠️ لازم تتقال: إلغاء الجلسات مقصود، مش عطل */}
              <div className="t-desc" style={{ marginTop: 12 }}>
                ولو كنت داخل على جهاز تاني، هيطلب منك تدخل تاني — ده مقصود.
              </div>
              <div style={{ marginTop: 24 }}>
                <button
                  type="button"
                  className="btn"
                  onClick={() => navigate(LOGIN_PATH)}
                >
                  صفحة الدخول
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate className="auth-form">
              <div style={{ marginTop: 32 }}>
                <div className="auth-title">حدّد كلمة السر</div>
                <div className="t-desc" style={{ marginTop: 8 }}>
                  اختار كلمة سر جديدة لحسابك على السيستم الإداري
                </div>
              </div>

              {error ? (
                <div
                  className="err-box"
                  style={{ marginTop: 20 }}
                  role="alert"
                  aria-live="assertive"
                >
                  <Icon name="alert" size={18} stroke="#B42318" />
                  <span className="t-body" style={{ color: "var(--danger)" }}>
                    {error}
                  </span>
                </div>
              ) : null}

              <div
                style={{
                  marginTop: 20,
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                }}
              >
                <div>
                  <label className="field-label" htmlFor="new-pw">
                    كلمة السر الجديدة
                  </label>
                  <div className="input" style={{ marginTop: 8 }}>
                    <Icon name="lock" size={16} stroke="#98A2B3" />
                    <input
                      id="new-pw"
                      type={showPw ? "text" : "password"}
                      autoComplete="new-password"
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      disabled={busy}
                    />
                    <PasswordReveal shown={showPw} onToggle={() => setShowPw((v) => !v)} disabled={busy} />
                  </div>
                  <div className="t-meta" style={{ marginTop: 6 }}>
                    {MIN_LENGTH} حروف أو أرقام على الأقل
                  </div>
                </div>

                <div>
                  <label className="field-label" htmlFor="new-pw2">
                    اكتبها تاني
                  </label>
                  <div className="input" style={{ marginTop: 8 }}>
                    <Icon name="lock" size={16} stroke="#98A2B3" />
                    <input
                      id="new-pw2"
                      type={showPw ? "text" : "password"}
                      autoComplete="new-password"
                      value={pw2}
                      onChange={(e) => setPw2(e.target.value)}
                      disabled={busy}
                    />
                    <PasswordReveal shown={showPw} onToggle={() => setShowPw((v) => !v)} disabled={busy} />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className={`btn${busy ? " is-busy" : ""}`}
                style={{ marginTop: 24 }}
                disabled={busy}
                aria-busy={busy}
              >
                {busy ? "بيتحفظ…" : "احفظ كلمة السر"}
              </button>

              <div className="t-meta" style={{ marginTop: 16, textAlign: "center" }}>
                <Link to={LOGIN_PATH} className="link">
                  الرجوع لصفحة الدخول
                </Link>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
